import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BannerSkeleton from './BannerSkeleton';
import { useCachedFetch } from '../../../hooks/useCachedFetch';
import { IMAGES_BASE_URL } from '../../../config/constants';
import './Banner.scss';

const Banner = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const location = useLocation();
  const navigate = useNavigate();

  const { data: promoData, loading: dataLoading, error: dataError } = useCachedFetch('/data/locales/promo.json');

  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [visible, setVisible] = useState(true);
  const [isClickable, setIsClickable] = useState(true);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError || !promoData?.comingSoonData || promoData.comingSoonData.length === 0) {
      setHasData(false);
      setIsLoading(false);
      return;
    }

    const latest = promoData.comingSoonData.reduce(
      (prev, curr) => (curr.comingSoonId > prev.comingSoonId ? curr : prev),
      promoData.comingSoonData[0]
    );

    const bannerData = latest?.bannerData?.[currentLang] || latest?.bannerData?.en;

    if (bannerData && bannerData.BannerText && latest) {
      setHasData(true);

      const hasValidSlug = latest?.slug && latest.slug.trim() !== '';
      setIsClickable(hasValidSlug);
    } else {
      setHasData(false);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, promoData, currentLang]);

  const latestItem = useMemo(() => {
    if (!promoData?.comingSoonData || promoData.comingSoonData.length === 0) return null;

    const latest = promoData.comingSoonData.reduce(
      (prev, curr) => (curr.comingSoonId > prev.comingSoonId ? curr : prev),
      promoData.comingSoonData[0]
    );

    return {
      ...latest,
      startDateTimestamp: new Date(latest.startDate).getTime(),
      endDateTimestamp: new Date(latest.endDate).getTime(),
      ...(latest.translations?.[currentLang] || {})
    };
  }, [promoData, currentLang]);

  const pageData = useMemo(() =>
    latestItem?.bannerData?.[currentLang] || latestItem?.bannerData?.en || null,
    [latestItem, currentLang]
  );

  useEffect(() => {
    if (!latestItem) return;

    const path = location.pathname;

    const comingSoonPath = `/${currentLang}/coming-soon`;
    const promoPath = `/${currentLang}/promo/`;

    if (!isClickable) {
      setVisible(true);
      return;
    }

    const shouldHide =
      path.includes(latestItem.slug) ||
      path.startsWith(comingSoonPath) ||
      path.startsWith(promoPath);

    setVisible(!shouldHide);
  }, [location.pathname, latestItem, currentLang, isClickable]);

  const handleBannerClick = useCallback((e) => {
    e.preventDefault();

    if (!isClickable || !latestItem) return;

    const now = Date.now();
    const start = latestItem.startDateTimestamp;

    if (now < start) {
      navigate(`/${currentLang}/coming-soon`);
    } else {
      navigate(`/${currentLang}/promo/${latestItem.slug}`);
    }
  }, [latestItem, currentLang, navigate, isClickable]);

  const bannerStyles = useMemo(() => {
    if (!pageData?.BannerLink) return {};

    const { bgColorBanner, textColor } = pageData.BannerLink;

    return {
      backgroundColor: bgColorBanner || 'transparent',
      textColor: textColor || '#fff',
    };
  }, [pageData]);

  const bannerPattern = useMemo(() => {
    if (!latestItem) return null;

    const itemTranslations = latestItem.translations?.[currentLang] || latestItem;
    const pattern = itemTranslations.bannerPattern || latestItem.bannerPattern;

    if (!pattern || pattern.trim() === '') return null;

    return pattern;
  }, [latestItem, currentLang]);

  const bannerAltText = useMemo(() => {
    if (!latestItem) return 'Banner pattern';

    const itemTranslations = latestItem.translations?.[currentLang] || latestItem;
    return itemTranslations.promoPageInfo?.buttonText || latestItem.promoPageInfo?.buttonText || 'Banner pattern';
  }, [latestItem, currentLang]);

  if (isLoading) {
    return <BannerSkeleton />;
  }

  if (!hasData || !pageData || !visible || !latestItem) {
    return null;
  }

  return (
    <div
      className={`hidden__banner ${!isClickable ? 'banner--info' : ''}`}
      style={{
        backgroundColor: bannerStyles.backgroundColor,
        cursor: isClickable ? 'pointer' : 'default'
      }}
      onClick={handleBannerClick}
    >
      {bannerPattern && (
        <img
          src={`${IMAGES_BASE_URL}${bannerPattern}`}
          alt={bannerAltText}
          className="banner__pattern__small"
          loading="lazy"
        />
      )}
      <span className="banner__text" style={{ color: bannerStyles.textColor }}>
        {pageData.BannerText}
      </span>
    </div>
  );
};

export default React.memo(Banner);